import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import WeekPicker from "../shared/WeekPicker";

const getToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getFortnightStart = (date: Date): Date => {
  // First week (Monday) of the fortnight
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7; // Mon=0, ..., Sun=6
  d.setDate(d.getDate() - offset);
  return d;
};

export const getFortnightEnd = (date: Date): Date => {
  const start = getFortnightStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 13);
  return end;
};

const addDays = (d: Date, n: number): Date => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const formatRange = (s: Date, e: Date) => {
  const o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  return `${s.toLocaleDateString("en-GB", o)} - ${e.toLocaleDateString("en-GB", o)}`;
};

interface FortnightlyInsightsHeaderProps {
  onFortnightChange?: (week1Start: Date, week2End: Date) => void;
  minDate?: Date;
}

const FortnightlyInsightsHeader: React.FC<FortnightlyInsightsHeaderProps> = ({
  onFortnightChange,
  minDate,
}) => {
  const [week1Start, setWeek1Start] = useState<Date>(() => {
    // Default: fortnight ending in the current week
    const currentWeekStart = getFortnightStart(getToday());
    return addDays(currentWeekStart, -7);
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleWeekSelect = (ws: Date) => {
    // Interpret picked week as WEEK 1; fortnight is week1 + next week
    setWeek1Start(ws);
    onFortnightChange?.(ws, addDays(ws, 13));
  };

  const shiftFortnight = (delta: number) => {
    const next = addDays(week1Start, delta * 14);
    const week2End = addDays(next, 13);
    if (delta > 0 && week2End > getToday()) return;
    handleWeekSelect(next);
  };

  const week1End = addDays(week1Start, 6);
  const week2Start = addDays(week1Start, 7);
  const week2End = addDays(week1Start, 13);

  const canGoNext = week2End < getToday();
  const canGoPrev = !minDate || week1Start > minDate;

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      <button
        onClick={() => canGoPrev && shiftFortnight(-1)}
        disabled={!canGoPrev}
        className={`p-1 rounded transition ${
          !canGoPrev ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
        }`}
      >
        <ChevronLeft className="w-4 h-4 text-gray-500" />
      </button>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-[13px] font-medium text-[#1F1F1F] font-inter whitespace-nowrap">
          {formatRange(week1Start, week1End)}
        </span>
        <span className="text-[13px] font-medium text-[#1F1F1F] font-inter whitespace-nowrap">
          {formatRange(week2Start, week2End)}
        </span>
        <Calendar className="w-4 h-4 text-[#00A9A9]" />
      </button>
      <button
        onClick={() => canGoNext && shiftFortnight(1)}
        disabled={!canGoNext}
        className={`p-1 rounded transition ${
          !canGoNext ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
        }`}
      >
        <ChevronRight className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 z-50">
          <WeekPicker
            selectedWeekStart={week1Start}
            onWeekSelect={handleWeekSelect}
            onClose={() => setOpen(false)}
            minDate={minDate}
            weeksCount={2}
          />
        </div>
      )}
    </div>
  );
};

export default FortnightlyInsightsHeader;
